import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useAuth } from '../contexts/AuthContext';
import { Send, Copy, Clock, Check } from 'lucide-react';
import '../styles/maya.css';

marked.setOptions({ breaks: true, gfm: true });

function Maya() {
  const { user, isAuthenticated } = useAuth();

  const [chat, setChat] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [welcomeContext, setWelcomeContext] = useState(null);
  const [welcomeLoading, setWelcomeLoading] = useState(false);
  const [imageModal, setImageModal] = useState({ show: false, url: '' });
  const [showHistory, setShowHistory] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);

  const messagesRef = useRef(null);
  const textareaRef = useRef(null);

  const renderMarkdown = useCallback((content) => {
    if (!content) return '';
    const md = content.replace(/\\n/g, '\n');
    return DOMPurify.sanitize(marked.parse(md));
  }, []);

  const hasConversation = chat.some(m => m.type === 'user');

  const fetchWelcome = useCallback(async () => {
    setWelcomeLoading(true);
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/maya_v2', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: 'welcome',
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-maya-session`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      const data = await result.json();
      if (Array.isArray(data) && data.length > 0) {
        const response = data[0].output || data[0].response;
        if (response) {
          setWelcomeContext(response);
        }
      }
    } catch (error) {
      console.error('Error fetching welcome message:', error);
    } finally {
      setWelcomeLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user && chat.length === 0) {
      fetchWelcome();
    }
  }, [isAuthenticated, user, chat.length, fetchWelcome]);

  useLayoutEffect(() => {
    if (messagesRef.current) {
      requestAnimationFrame(() => {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      });
    }
  }, [chat, loading]);

  const fetchChatHistory = useCallback(async () => {
    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/maya-chat-history', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId: user?.id || 'matthew' })
      });
      const data = await result.json();
      const convos = Array.isArray(data) ? data[0]?.conversations : data?.conversations;
      if (convos && convos.length > 0) {
        setChatHistory(convos.map(c => ({
          id: c.id,
          title: c.title,
          lastMessage: c.preview || '',
          messageCount: c.messages?.length || 0,
          messages: c.messages || []
        })));
      }
    } catch (err) {
      console.error('Error fetching chat history:', err);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchChatHistory();
    }
  }, [isAuthenticated, user, fetchChatHistory]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: query.trim(),
      timestamp: new Date()
    };

    setChat(prevChat => [...prevChat, userMessage]);
    const currentQuery = query;
    setQuery('');
    setLoading(true);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 600000);

    try {
      const result = await fetch('https://n8n.iniai.cloud/webhook/maya_v2', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
        },
        signal: controller.signal,
        body: JSON.stringify({
          type: 'query',
          query: currentQuery,
          user: {
            id: user?.id || '',
            email: user?.email || '',
            role: user?.role || '',
            name: user?.name || ''
          },
          sessionId: `${user?.id || 'anonymous'}-maya-session`,
          context: {
            isAuthenticated,
            timestamp: new Date().toISOString()
          }
        })
      });

      clearTimeout(timeoutId);
      const data = await result.json();

      if (Array.isArray(data) && data.length > 0) {
        const rawContent = data[0].response || data[0].output;
        const assistantMessage = {
          id: Date.now() + 1,
          type: 'maya',
          content: rawContent,
          timestamp: new Date()
        };
        setChat(prevChat => [...prevChat, assistantMessage]);
        fetchChatHistory();
      }
    } catch (error) {
      clearTimeout(timeoutId);

      const errorText = error.name === 'AbortError'
        ? 'This query is taking longer than expected. Try simplifying your question.'
        : 'An error occurred while processing your request.';

      setChat(prevChat => [...prevChat, {
        id: Date.now() + 1,
        type: 'error',
        content: errorText,
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleTextareaChange = (e) => {
    setQuery(e.target.value);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 140) + 'px';
    }
  };

  const copyToClipboard = useCallback((messageId, content) => {
    const cleanText = content.replace(/<[^>]*>?/gm, '');
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cleanText).then(() => {
        setCopiedId(messageId);
        setTimeout(() => setCopiedId(null), 1500);
      }).catch(() => {});
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = cleanText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        setCopiedId(messageId);
        setTimeout(() => setCopiedId(null), 1500);
      } catch (err) {
        // silent fail
      }
      document.body.removeChild(textarea);
    }
  }, []);

  const openImageModal = useCallback((imageUrl) => {
    setImageModal({ show: true, url: imageUrl });
  }, []);

  const closeImageModal = () => {
    setImageModal({ show: false, url: '' });
  };

  useEffect(() => {
    window.openImageModal = openImageModal;
    return () => { delete window.openImageModal; };
  }, [openImageModal]);

  const startNewChat = () => {
    setChat([]);
    setCurrentChatId(null);
    setShowHistory(false);
    setWelcomeContext(null);
    fetchWelcome();
  };

  const loadChat = (chatId) => {
    const savedChat = chatHistory.find(c => c.id === chatId);
    if (savedChat) {
      setChat(savedChat.messages);
      setCurrentChatId(chatId);
      setShowHistory(false);
    }
  };

  // --- Render ---

  if (!hasConversation && !loading) {
    return (
      <div className="maya-workspace">
        <div className="maya-header">
          <div className="maya-header-left">
            <span className="maya-header-title">Maya</span>
          </div>
          <div className="maya-header-actions">
            <button className="maya-header-btn" onClick={() => setShowHistory(true)}>History</button>
            <button className="maya-header-btn" onClick={startNewChat}>New</button>
          </div>
        </div>

        <div className="maya-welcome">
          <h1 className="maya-welcome-name">Maya</h1>
          <hr className="maya-welcome-divider" />
          <p className="maya-welcome-prompt">What are we planning today?</p>

          {welcomeLoading && (
            <div className="maya-welcome-context">
              <span className="maya-thinking-text">Thinking</span>
            </div>
          )}

          {!welcomeLoading && welcomeContext && (
            <div
              className="maya-welcome-context"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(welcomeContext) }}
            />
          )}

          <div className="maya-welcome-input-wrapper">
            <div className="maya-input-container">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={handleTextareaChange}
                onKeyDown={handleKeyDown}
                placeholder="Ask Maya..."
                className="maya-textarea"
                rows={1}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!query.trim()}
                className="maya-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>

        {showHistory && (
          <div className="maya-history-overlay">
            <div className="maya-history-panel">
              <div className="maya-history-header">
                <h2>History</h2>
                <button className="maya-history-close" onClick={() => setShowHistory(false)}>&times;</button>
              </div>
              <div className="maya-history-list">
                {chatHistory.length === 0 && (
                  <div className="maya-history-empty">No conversations yet</div>
                )}
                {chatHistory.map((saved) => (
                  <div
                    key={saved.id}
                    className={`maya-history-item ${currentChatId === saved.id ? 'active' : ''}`}
                    onClick={() => loadChat(saved.id)}
                  >
                    <div className="maya-history-item-title">{saved.title}</div>
                    <div className="maya-history-item-date">
                      <Clock size={11} />
                      <span>{saved.messageCount || 0} messages</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="maya-history-scrim" onClick={() => setShowHistory(false)} />
          </div>
        )}

        {imageModal.show && (
          <div className="maya-image-modal" onClick={closeImageModal}>
            <img src={imageModal.url} alt="Full size" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="maya-workspace">
      <div className="maya-header">
        <div className="maya-header-left">
          <span className="maya-header-title">Maya</span>
        </div>
        <div className="maya-header-actions">
          <button className="maya-header-btn" onClick={() => setShowHistory(true)}>History</button>
          <button className="maya-header-btn" onClick={startNewChat}>New</button>
        </div>
      </div>

      <div className="maya-messages-area" ref={messagesRef}>
        <div className="maya-messages-wrapper">
          {chat.map((message) => {
            if (message.type === 'user') {
              return (
                <div key={message.id} className="maya-exchange">
                  <div className="maya-user-message">
                    <div className="maya-user-message-content">{message.content}</div>
                  </div>
                </div>
              );
            }

            if (message.type === 'error') {
              return (
                <div key={message.id} className="maya-exchange">
                  <div className="maya-error">{message.content}</div>
                </div>
              );
            }

            return (
              <div key={message.id} className="maya-exchange">
                <div className="maya-response">
                  <div
                    className="maya-response-content"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
                  />
                  <div className="maya-response-actions">
                    <button
                      className="maya-action-btn"
                      onClick={() => copyToClipboard(message.id, message.content)}
                      title="Copy"
                    >
                      {copiedId === message.id ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <span className={`maya-copy-tooltip ${copiedId === message.id ? 'visible' : ''}`}>
                      Copied
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {loading && (
            <div className="maya-thinking">
              <span className="maya-thinking-text">Thinking</span>
            </div>
          )}
        </div>
      </div>

      <div className="maya-input-area">
        <div className="maya-input-wrapper">
          <div className="maya-input-container">
            <textarea
              ref={textareaRef}
              value={query}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder="Ask Maya..."
              className="maya-textarea"
              rows={1}
              disabled={loading}
            />
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!query.trim() || loading}
              className="maya-send-btn"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>

      {showHistory && (
        <div className="maya-history-overlay">
          <div className="maya-history-panel">
            <div className="maya-history-header">
              <h2>History</h2>
              <button className="maya-history-close" onClick={() => setShowHistory(false)}>&times;</button>
            </div>
            <div className="maya-history-list">
              {chatHistory.length === 0 && (
                <div className="maya-history-empty">No conversations yet</div>
              )}
              {chatHistory.map((saved) => (
                <div
                  key={saved.id}
                  className={`maya-history-item ${currentChatId === saved.id ? 'active' : ''}`}
                  onClick={() => loadChat(saved.id)}
                >
                  <div className="maya-history-item-title">{saved.title}</div>
                  <div className="maya-history-item-date">
                    <Clock size={11} />
                    <span>{saved.messageCount || 0} messages</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="maya-history-scrim" onClick={() => setShowHistory(false)} />
        </div>
      )}

      {imageModal.show && (
        <div className="maya-image-modal" onClick={closeImageModal}>
          <img src={imageModal.url} alt="Full size" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

export default Maya;
